import { useContext, useEffect, useMemo, useState } from 'react';
import Stack from '@mui/joy/Stack';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import { Avatar, Box, Breadcrumbs, Button, Chip, IconButton, Input, ListItem, ListItemButton, Option, Select } from '@mui/joy';
import List from '@mui/joy/List';

import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

import { motion, AnimatePresence } from 'framer-motion';

import { toggleMessagesPane } from '../../utils';
import ChatListItem from './ChatListItem';
import { Link } from 'react-router';
import { APIGetChats, APICreateChatWithProfile, APIGetProfilesWithoutDM, APIGetUnreadCounts, APIMarkMessagesAsRead, APIGetDoctors } from '../../api';
import { LayoutContext } from '../../context';



export default function ChatsPane(props) {
  const { chats, setChats, loadsUnreadCounts, selectedChatId, handleChatSelect, onNewChatCreated } = props;
  const { token, account, isPatient } = useContext(LayoutContext);
  
  const [contactsModalOpen, setContactsModalOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [doctorsPage, setDoctorsPage] = useState(1);
  const [doctorsHasMore, setDoctorsHasMore] = useState(true);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  // Загрузки счетчиков при изменении списка пациентов
  useEffect(() => {
    const _load = async () => {
      const data = await APIGetChats(token);
      // Добавляем флаг has_unread к каждому чату
      // const chatsWithUnread = data?.chats?.map(chat => ({
      //   ...chat,
      //   has_unread: getUnreadCountForChat(chat.chat_id) > 0
      // }));
      setChats(data?.chats?.reverse());
    }
    _load();
    // loadsUnreadCounts();
  }, []);

  const handleChatCreated = (chatId) => {
    // Перезагружаем чаты после создания нового
    APIGetChats(token).then(data => {
      setChats(data?.chats?.reverse());
      // Обновляем счетчики непрочитанных
      loadsUnreadCounts();
      // Вызываем callback для уведомления родительского компонента
      if (onNewChatCreated) {
        onNewChatCreated(chatId);
      }
    });
  };

  // Внутри компонента ChatsPane, перед рендерингом чатов:
  const sortedChats = useMemo(() => {
    return [...chats].sort((a, b) => {
      const dateA = new Date(a.last_message?.date_create || a.date_create || 0);
      const dateB = new Date(b.last_message?.date_create || b.date_create || 0);
      return dateB.getTime() - dateA.getTime(); // Новые сверху
    });
  }, [chats]);


  const loadDoctors = async (page = 1, append = false) => {
    if (doctorsLoading) return;
    setDoctorsLoading(true);
    try {
      const data = await APIGetDoctors(token, { page, page_size: 20 });
      const newDoctors = data?.doctors || [];
      setDoctors(prev => append ? [...prev, ...newDoctors] : newDoctors);
      setDoctorsPage(page);
      // Если получили меньше page_size или достигли count — больше нет
      const totalLoaded = append ? doctors.length + newDoctors.length : newDoctors.length;
      setDoctorsHasMore(totalLoaded < (data?.count || 0));
    } catch (e) {
      console.error('Ошибка загрузки врачей:', e);
    } finally {
      setDoctorsLoading(false);
    }
  };

  useEffect(() => {
    if (!isPatient) {
      loadDoctors(1, false);
    }
  }, []);

  const handleDoctorsListScroll = (e) => {
    const listbox = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = listbox;
    // Когда до конца осталось менее 50px — подгружаем
    if (scrollHeight - scrollTop - clientHeight < 50 && doctorsHasMore && !doctorsLoading) {
      loadDoctors(doctorsPage + 1, true);
    }
  };

  // Применить фильтры — запрос на сервер
  const applyFilters = async (query, doctorId) => {
    setIsFiltering(true);
    try {
      const params = {};
      if (query?.trim()) params.search = query.trim();
      if (doctorId) params.doctor_id = doctorId;
      const data = await APIGetChats(token, params);
      setChats(data?.chats?.reverse() || []);
    } catch (e) {
      console.error('Ошибка фильтрации чатов:', e);
    } finally {
      setIsFiltering(false);
    }
  };

  // Сброс фильтров
  const resetFilters = () => {
    setSearchQuery('');
    setSelectedDoctorId(null);
    APIGetChats(token).then(data => setChats(data?.chats?.reverse() || []));
  };

  const hasActiveFilters = searchQuery.trim() || selectedDoctorId;


  return (
    <>
      <Sheet
        sx={{
          borderRight: '1px solid',
          borderColor: 'divider',
          height: { xs: 'calc(100dvh - var(--Header-height))', md: '100dvh' },
          // height: '100dvh',
          overflowY: 'auto',
        }}
      >
        <Stack
          direction="column"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'space-between', width: "100%", p: 2, pt: 1, pb: 0 }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', justifyContent: 'space-between', width: "100%", pt: 1, pb: 0 }}
          >
            <Stack direction={'row'}>
              <Typography
                endDecorator={
                  <Chip variant="soft" color="primary" size="md" slotProps={{ root: { component: 'span' } }}>
                    {chats?.length}
                  </Chip>
                }
                level="h2"
                sx={{ mr: 'auto' }}
              >
                Чаты
              </Typography>
            </Stack>
            {account && !isPatient && (
              <Button
                fullWidth
                startDecorator={<PersonAddAltIcon />}
                onClick={() => setContactsModalOpen(!contactsModalOpen)}
                variant="outlined"
                size="md"
                sx={{ width: 'auto' }}
              >
                Контакты
              </Button>
            )}
          </Stack>

          {/* Фильтры */}
          {!isPatient && (
            <Box sx={{ width: "100%", pt: 1.5, pb: 0 }}>
              <Stack spacing={1}>
                {/* Поиск */}
                <Input
                  size="sm"
                  placeholder="Поиск по имени, чату или ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyFilters(searchQuery, selectedDoctorId);
                  }}
                  startDecorator={<SearchRoundedIcon />}
                  endDecorator={
                    searchQuery ? (
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => {
                          setSearchQuery('');
                          applyFilters('', selectedDoctorId);
                        }}
                      >
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    ) : null
                  }
                />

                {/* Фильтр по врачу + кнопки */}
                <Stack direction="row" spacing={1}>
                  <Select
                    size="sm"
                    placeholder="Фильтр по врачу"
                    value={selectedDoctorId}
                    onChange={(_, val) => {
                      setSelectedDoctorId(val);
                      applyFilters(searchQuery, val);
                    }}
                    slotProps={{
                      listbox: {
                        sx: { zIndex: 10021, maxHeight: 300, overflow: 'auto' },
                        onScroll: handleDoctorsListScroll,
                      }
                    }}
                    sx={{ flex: 1 }}
                    {...(selectedDoctorId ? {
                      endDecorator: (
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={() => {
                            setSelectedDoctorId(null);
                            applyFilters(searchQuery, null);
                          }}
                        >
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      )
                    } : {})}
                  >
                    {doctors.map(doc => (
                      <Option key={doc.id} value={doc.id}>{doc.name}</Option>
                    ))}
                    {doctorsLoading && (
                      <Option disabled value="__loading__">Загрузка...</Option>
                    )}
                  </Select>

                  <Button
                    size="sm"
                    variant="solid"
                    color="primary"
                    onClick={() => applyFilters(searchQuery, selectedDoctorId)}
                    disabled={isFiltering}
                  >
                    Найти
                  </Button>

                  {hasActiveFilters && (
                    <Button
                      size="sm"
                      variant="soft"
                      color="neutral"
                      onClick={resetFilters}
                    >
                      Сброс
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>

        <Box sx={{ m: 2 }}>
          <CollabeContact
            open={contactsModalOpen}
            onClose={() => setContactsModalOpen(false)}
            onChatCreated={handleChatCreated}
          />
        </Box>

        {/* <Box sx={{ px: 2, pb: 1.5 }}>
          <Input
            size="sm"
            startDecorator={<SearchRoundedIcon />}
            placeholder="Поиск"
            aria-label="Поиск"
          />
        </Box> */}
        <List
          sx={{
            py: 0,
            '--ListItem-paddingY': '0.75rem',
            '--ListItem-paddingX': '1rem',
          }}
        >
          {sortedChats?.map((chat) => (
            <ChatListItem
              key={chat.chat_id}
              chat={chat}
              onSelect={handleChatSelect}
              selected={selectedChatId === chat.chat_id}
            />
          ))}
        </List>
      </Sheet>
    </>
  );
}





function CollabeContact({ open, onClose, onChatCreated }) {
  const { token, account } = useContext(LayoutContext);
  const [profiles, setProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadProfiles();
    }
  }, [open]);

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await APIGetProfilesWithoutDM(token);
      setProfiles(data.profiles || []);
    } catch (error) {
      console.error('Ошибка загрузки сотрудников:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChat = async (profileId) => {
    try {
      setLoading(true);
      const response = await APICreateChatWithProfile(profileId, token);
      
      if (response.chat_id) {
        onChatCreated(response.chat_id);
        onClose();
      }
    } catch (error) {
      console.error('Ошибка создания чата:', error);
      alert('Ошибка при создании чата');
    } finally {
      setLoading(false);
    }
  };

  function GetPosition(position) {
    if (position?.includes('manager')) return "Менеджер";
    if (position?.includes('surveyor')) return "Обмерщик";
    if (position?.includes('draftsman')) return "Чертежник";
    return "-";
  }

  const filteredProfiles = profiles.filter(profile =>
    profile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (Array.isArray(profile.position) && 
    profile.position.some(position => 
      position.toLowerCase().includes(searchTerm.toLowerCase())
    ))
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Sheet variant='outlined' sx={{ borderRadius: 'lg', p: 2 }}>
            <Typography level="h4" sx={{ mb: 2 }}>
              Контакты
            </Typography>

            <Input
              placeholder="Поиск сотрудников..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />

            {loading ? (
              <Typography>Загрузка...</Typography>
            ) : (
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {filteredProfiles.map((profile) => (
                  <ListItem key={profile.id}>
                    <ListItemButton
                      onClick={() => handleCreateChat(profile.id)}
                      disabled={loading}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <Avatar size="sm" />
                      <Box>
                        <Typography level="body-sm" fontWeight="bold">
                          {profile.name}
                        </Typography>
                        <Typography level="body-xs" color="primary">
                          {GetPosition(profile.position)}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}

            {filteredProfiles.length === 0 && !loading && (
              <Typography level="body-sm" sx={{ textAlign: 'center', py: 2 }}>
                Нет доступных сотрудников для создания чата
              </Typography>
            )}
          </Sheet>
        </motion.div>
      )}
    </AnimatePresence>
  );
}